import { IDimensions, IDimensionsPixel, IMobileConfig, ICanvasSize } from "./interface";

export enum Device {
  DESKTOP = "desktop",
  MOBILE = "mobile",
  TABLET = "tablet"
}

export enum Orientation {
  PORTRAIT = "portrait",
  LANDSCAPE = "landscape"
}

export enum KeypadType {
  KCUI = "kcui",
  PLAYUI = "playui"
}

export function isPortrait(
  viewportSize: IDimensions,
  keypadType: string,
  isLegacy: boolean,
  platform: string
): boolean {
  if (isLegacy) {
    if(keypadType === KeypadType.PLAYUI){
      return viewportSize.width < viewportSize.height && (platform === Device.MOBILE || platform === Device.TABLET);
    } else {
      return viewportSize.width < viewportSize.height && platform === Device.MOBILE;
    }
  } else {
    return viewportSize.width < viewportSize.height;
  }
}

export function ratio16x9(width: number): number {
  return Math.round((width / 16) * 9);
}

export function ratio9x16(width: number): number {
  return Math.round((width / 9) * 16);
}

export function getContainer(win: Window): string {
  if (win !== win.parent) {
    return "iframe";
  }
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  else if (win.navigator.standalone || win.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  } else {
    return "";
  }
}

export function get16x9Dimensions(
  viewportSize: IDimensions,
  orientation: string,
  isCustomSize: boolean,
  platform: string
): IDimensions {
  let width, height;

  if (orientation === Orientation.LANDSCAPE) {
    width = Math.ceil(Math.min(ratio9x16(viewportSize.height), viewportSize.width));
    height = ratio16x9(width);
  } else {
    if ((platform === Device.MOBILE || platform === Device.TABLET) && isCustomSize) {
      width = Math.ceil(Math.min(ratio16x9(viewportSize.height), viewportSize.width));
      height = ratio9x16(width);
    } else {
      width = viewportSize.width;
      height = ratio16x9(viewportSize.width);
    }
  }

  return {
    width,
    height
  };
}

export function addPropertiesToDocument(
  docElement: HTMLElement,
  viewportSize: IDimensions,
  gameViewportSize: IDimensions
): void {
  docElement.style.setProperty("--lm-viewport-width", `${viewportSize.width.toFixed(0)}px`);
  docElement.style.setProperty("--lm-viewport-height", `${viewportSize.height.toFixed(0)}px`);

  docElement.style.setProperty("--lm-width", `${gameViewportSize.width.toFixed(0)}px`);
  docElement.style.setProperty("--lm-height", `${gameViewportSize.height.toFixed(0)}px`);
}

export function addClassToBody(
  bodyElement: HTMLElement,
  isPortrait: boolean,
  platform: string,
  isCustomCanvasSizeAvailable: boolean
): void {
  if (isPortrait) {
    bodyElement.classList.remove("lm-landscape");
    bodyElement.classList.add("lm-portrait");
    // added legacy classes to support sweden plugin
    bodyElement.classList.remove(`${platform}_landscape`);
    bodyElement.classList.add(`${platform}_portrait`);
  } else {
    bodyElement.classList.remove("lm-portrait");
    bodyElement.classList.add("lm-landscape");
    // added legacy classes to support sweden plugin
    bodyElement.classList.remove(`${platform}_portrait`);
    bodyElement.classList.add(`${platform}_landscape`);
  }
  // additional class for games with custom canvas size
  if (isCustomCanvasSizeAvailable && platform === Device.MOBILE) {
    bodyElement.classList.add("lm-custom-canvas");
  }
}

export function addOSSpecificClass(bodyElement: HTMLElement, isiOSDevice: boolean): void {
  if (isiOSDevice) {
    bodyElement.classList.add("iOS");
    bodyElement.classList.add("iOS_ui");
  }
}

export function addPlatformClass(docElement: HTMLElement, platform: string) {
  if (docElement.classList.contains("lm-mobile")) {
    docElement.classList.remove("lm-mobile");
  } else if (docElement.classList.contains("lm-tablet")) {
    docElement.classList.remove("lm-tablet");
  } else if (docElement.classList.contains("lm-desktop")) {
    docElement.classList.remove("lm-desktop");
  }

  docElement.classList.add("layout-manager");
  docElement.classList.add(`lm-${platform}`);
}

export function addAlignmentClass(
  bodyElement: HTMLElement,
  viewportWidth: number,
  gameViewWidth: number,
  isPortrait: boolean
): void {
  if (viewportWidth > gameViewWidth) {
    bodyElement.classList.remove("lm-vertical-center");
    bodyElement.classList.add("lm-horizontal-center");
  } else if (isPortrait && viewportWidth === gameViewWidth) {
    bodyElement.classList.remove("lm-vertical-center");
    bodyElement.classList.remove("lm-horizontal-center");
  } else {
    bodyElement.classList.remove("lm-horizontal-center");
    bodyElement.classList.add("lm-vertical-center");
  }
}

export function getViewPortDimensions(
  docElement: HTMLElement,
  win: Window,
  platform: string,
  safeArea: number,
  isSafari: boolean
): IDimensions {
  const docHeight = docElement.clientHeight;
  const docWidth = docElement.clientWidth;

  const width = docWidth < win.innerWidth ? win.innerWidth : docWidth;
  let height = docHeight < win.innerHeight ? win.innerHeight : docHeight;

  // calculate viewport height without safe area
  if (platform !== Device.DESKTOP) {
    // this is to fix height in safari landscape
    if (platform === Device.MOBILE && isSafari && width > height) {
      height = win.innerHeight;
    }

    height = height - safeArea;
  }

  return {
    width,
    height
  };
}

export function getSafeArea(win: Window, area: string): number {
  const propertyValue = `--safe-area-inset-${area}`;
  const safeArea = win.getComputedStyle(document.documentElement).getPropertyValue(propertyValue);

  if (!safeArea) {
    return 0;
  } else {
    return parseInt(safeArea, 10);
  }
}

export function getOrientation(isPortrait: boolean): string {
  return isPortrait ? Orientation.PORTRAIT : Orientation.LANDSCAPE;
}

export function getCanvasSize(
  canvasSize: ICanvasSize,
  platform: string,
  orientation: string
): IDimensionsPixel {
  if (platform === Device.MOBILE || platform === Device.TABLET) {
    return orientation in canvasSize[platform]
      ? (canvasSize[platform] as IMobileConfig)[orientation]
      : (canvasSize[platform] as IDimensionsPixel);
  } else {
    return canvasSize[platform] as IDimensionsPixel;
  }
}

export function getBaseResolution(
  canvasSize: ICanvasSize,
  isPortrait: boolean,
  isCustomCanvasSizeAvailable: boolean,
  platform: string
) {
  const changedValues = {
    width: canvasSize.base.height,
    height: canvasSize.base.width
  };

  // this condition is to update base size in portrait mode
  if ((platform === Device.MOBILE || platform === Device.TABLET) && isPortrait && isCustomCanvasSizeAvailable) {
    return changedValues;
  } else {
    return canvasSize.base;
  }
}

export function getScaleFactor(
  platformResolution: IDimensions,
  baseResolution: IDimensions
): number {
  return Number((platformResolution.height / baseResolution.height).toFixed(2));
}

export function isCustomCanvasSizeAvailable(canvasSize: ICanvasSize, orientation: string, platform: string): boolean {
  return (
    canvasSize !== undefined && canvasSize !== null && orientation in canvasSize[platform]
  );
}

export function getScale(gameViewportSize: IDimensions, baseResolution: IDimensions): number {
  const scaleX = gameViewportSize.width / baseResolution.width;
  const scaleY = gameViewportSize.height / baseResolution.height;

  return Number(Math.min(scaleX, scaleY).toFixed(8));
}

export function getGameWrapperSize(gameElement: HTMLElement): IDimensions {
  const { clientWidth: width, clientHeight: height } = gameElement;

  return {
    width,
    height
  };
}
